import React, { useEffect } from "react";
import styled from "styled-components";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePostHog } from "posthog-js/react";
import { useDispatch, useSelector } from "react-redux";
import { InsightsReportScheduleTypeEnum, InsightsScheduleDestinationEnum, StatusStateEnum } from "~/types/enums";
import Modal from "~/components-shared/Modal";
import toastsActionCreators from "~/store/toasts/actions";
import { Alert } from "~/components-shared";
import moment from "moment";
import InsightsReportForm from "~/forms/InsightsReportForm";
import insightsSelectors from "~/store/insights/selectors";
import insightsActionCreators from "~/store/insights/actions";
import insightsReportSchema from "~/forms/InsightsReportForm/insightsReportSchema";
const Sticky = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
`;
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  height: 100%;
`;
const InsightsReportUpdateModal = () => {
  const {
    t
  } = useTranslation();
  const action = useSelector(insightsSelectors.getAction);
  const config = useSelector(insightsSelectors.getSelected);
  const configId = config?.id || "";
  const posthog = usePostHog();
  const status = useSelector(insightsSelectors.getStatusByConfig(configId));
  const error = useSelector(insightsSelectors.getErrorByConfig(configId));
  const dispatch = useDispatch();
  const defaultValues = {
    name: config?.name || "",
    topicIds: config?.topics.map(topic => topic.id) || [],
    config: config?.config?.map(_ref => {
      let {
        type,
        size
      } = _ref;
      return {
        type,
        size
      };
    }),
    scheduleType: config?.scheduleType || InsightsReportScheduleTypeEnum.WEEKLY,
    scheduleDayOfWeek: config?.scheduleDayOfWeek || 4,
    scheduleTimeOfDay: moment.utc(config?.scheduleTimeOfDay || "10:00:00", "hh:mma").toISOString(),
    scheduleDestination: config?.scheduleDestination || InsightsScheduleDestinationEnum.SLACK
  };
  const {
    handleSubmit,
    control,
    reset,
    watch
  } = useForm({
    resolver: yupResolver(insightsReportSchema),
    defaultValues
  });
  useEffect(() => {
    if (!action) {
      reset(defaultValues);
    }
  }, [action]);
  const onSubmit = handleSubmit(data => {
    dispatch(insightsActionCreators.updateConfigStarted(configId, data, _ref2 => {
      let {
        data: newConfig
      } = _ref2;
      if (newConfig) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("InsightsReportUpdateModal.reportUpdated", {
            name: newConfig.name
          }),
          timeout: 10000
        }));
        reset(defaultValues);
        posthog.capture(`Insights Report Update – Save`, {
          configId,
          ...data
        });
      }
    }));
  });
  const onCancel = () => {
    dispatch(insightsActionCreators.actionChanged(null, null));
    posthog.capture(`Insights Report Update – Cancel`, {
      configId
    });
  };
  return /*#__PURE__*/React.createElement(Modal.Body, {
    title: t("InsightsReportUpdateModal.updateReport"),
    onClose: onCancel,
    maxWidth: "640px",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Modal.Content, null, error && /*#__PURE__*/React.createElement(Sticky, null, /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error)), /*#__PURE__*/React.createElement(InsightsReportForm, {
    control: control,
    watch: watch,
    config: config
  })), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: onCancel,
    rounded: true
  }, t("common.actions.cancel")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    type: "submit",
    maxWidth: "230px",
    onClick: onSubmit,
    s: {
      ml: 2
    },
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("common.actions.save")))));
};
export default InsightsReportUpdateModal;